export type EnvironmentalMetricId = 'temperature' | 'humidity' | 'emc' | 'dew-point' | 'dew-point-margin';
export type EnvironmentalMetricStatus = 'alarm' | 'warning' | 'expected';
export type EnvironmentalTimeRangeId = '15-minutes' | '1-day' | '1-week' | '1-month' | '3-months';

export type EnvironmentalMetric = {
    id: EnvironmentalMetricId;
    label: string;
    value: string;
    unit: string;
    status: EnvironmentalMetricStatus;
    statusLabel: string;
    warningThreshold: number;
    alarmThreshold: number;
};

export type EnvironmentalNotification = {
    id: string;
    metricId: EnvironmentalMetricId;
    title: string;
    severity: 'alarm' | 'warning';
    value: string;
    unit: string;
    time: string;
    date: string;
    message: string;
    recommendation: string;
    eventDetails?: {
        average: string;
        integrated: string;
        threshold: string;
    };
};

export type EnvironmentalChartPoint = {
    date: string;
    timestamp: string;
    temperature: number;
    humidity: number;
    emc: number;
    dewPoint: number;
    dewPointMargin: number;
};

export type EnvironmentalMonthlyChartPoint = {
    month: string;
    temperature: number;
    humidity: number;
    emc: number;
    dewPoint: number;
    dewPointMargin: number;
};

export type EnvironmentalTimeRange = {
    id: EnvironmentalTimeRangeId;
    label: string;
};

export type EnvironmentalNotificationHistoryItem = {
    id: string;
    metricId: EnvironmentalMetricId;
    severity: 'alarm' | 'warning' | 'normal' | 'offline';
    title: string;
    time: string;
    date: string;
    description?: string;
    message: string;
};

export type EnvironmentalStressMonitoringRules = {
    samplingIntervalMinutes: number;
    notificationTriggerConsecutivePoints: number;
    severityMapping: {
        warning: string;
        alarm: string;
    };
};

export type EnvironmentalStressMonitoringData = {
    metrics: EnvironmentalMetric[];
    notifications: EnvironmentalNotification[];
    chartPoints: EnvironmentalChartPoint[];
    chartPointsByRange?: Partial<Record<EnvironmentalTimeRangeId, EnvironmentalChartPoint[]>>;
    monthlyChartPoints: EnvironmentalMonthlyChartPoint[];
    timeRanges: EnvironmentalTimeRange[];
    notificationHistory: EnvironmentalNotificationHistoryItem[];
    rules: EnvironmentalStressMonitoringRules;
};

export type EnvironmentalStressMonitoringApiResponse = {
    success: boolean;
    data: EnvironmentalStressMonitoringData;
    requestId?: string;
};

export const getChartPointsForRange = (
    data: EnvironmentalStressMonitoringData,
    rangeId: EnvironmentalTimeRangeId
): EnvironmentalChartPoint[] => data.chartPointsByRange?.[rangeId] ?? data.chartPoints;
